import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { CrewBasicInfo } from "../domain/CrewBasicInfo.js";
// 船員マスターリポジトリ
const enterpriseCrewRepositoryV2 = AiseaApiFactory.get("enterpriseCrew_v2");
const enterpriseCrewRepository = AiseaApiFactory.get("enterpriseCrew");

const enterpriseCrewRoleRepository = AiseaApiFactory.get("enterpriseCrewRole");

/**
 * affiliated_companiesを整形（空行削除 & start_date昇順ソート）
 * @param {Array} affiliatedCompanies
 * @returns {Array}
 */
const normalizeAffiliatedCompanies = affiliatedCompanies => {
  if (!affiliatedCompanies || !Array.isArray(affiliatedCompanies)) {
    return affiliatedCompanies;
  }

  // 要素がすべて「false」とみなせる場合（null, undefined, 空文字, 空配列）は行自体を削除
  const filtered = affiliatedCompanies.filter(item => {
    if (!item || typeof item !== "object") {
      return false;
    }

    const values = Object.values(item);
    const hasAnyValue = values.some(val => {
      if (val === null || val === undefined) {
        return false;
      }
      if (typeof val === "string") {
        return val.trim() !== "";
      }
      if (Array.isArray(val)) {
        return val.length > 0;
      }
      // number, boolean, objectなどはそのままtruthy判定に任せる
      return !!val;
    });

    return hasAnyValue;
  });

  // start_dateの昇順でソート（古い日付が先頭）
  filtered.sort((a, b) => {
    const aDate = a.start_date ? new Date(a.start_date) : null;
    const bDate = b.start_date ? new Date(b.start_date) : null;

    if (!aDate && !bDate) return 0;
    if (!aDate) return 1; // 日付なしは後ろへ
    if (!bDate) return -1;

    return aDate - bDate; // 昇順
  });

  return filtered;
};

/**
 * crew_available_rolesをavailable_roles形式に変換
 * @param {Array} crewAvailableRoles - APIから返ってくるcrew_available_roles
 * @returns {Array} available_roles形式の配列
 */
const transformAvailableRoles = crewAvailableRoles => {
  if (!crewAvailableRoles || !Array.isArray(crewAvailableRoles)) {
    return [];
  }

  // is_appointedがtrueのアイテムをベースにする
  const appointedItems = crewAvailableRoles.filter(
    item => item.is_appointed === true
  );

  return appointedItems.map(appointedItem => {
    const result = {
      role_id: appointedItem.enterprise_crew_role_id,
      start_date: appointedItem.start_date,
      available_role_id: appointedItem.enterprise_crew_role_id,
    };

    // 同じstart_dateでis_appointedがfalseのアイテムを探す
    const availableRoleItem = crewAvailableRoles.find(
      item =>
        item.is_appointed === false &&
        item.start_date === appointedItem.start_date
    );

    if (availableRoleItem) {
      result.available_role_id = availableRoleItem.enterprise_crew_role_id;
    }

    return result;
  });
};

/**
 * available_rolesをcrew_available_roles形式に変換
 * @param {Array} availableRoles - available_roles形式の配列
 * @returns {Array} crew_available_roles形式の配列
 */
const transformToCrewAvailableRoles = availableRoles => {
  if (!availableRoles || !Array.isArray(availableRoles)) {
    return [];
  }

  // start_dateの昇順でソート（古い日付が先頭）
  const sortedAvailableRoles = [...availableRoles].sort((a, b) => {
    const aDate = a.start_date ? new Date(a.start_date) : null;
    const bDate = b.start_date ? new Date(b.start_date) : null;

    if (!aDate && !bDate) return 0;
    if (!aDate) return 1; // 日付なしは後ろへ
    if (!bDate) return -1;

    return aDate - bDate; // 昇順
  });

  const result = [];

  sortedAvailableRoles.forEach(item => {
    if (!item.start_date || !item.role_id) {
      return; // start_dateとrole_idは必須
    }

    const roleId = item.role_id;
    const availableRoleId = item.available_role_id;
    const startDate = item.start_date;

    // available_role_idとrole_idが同一の場合
    if (availableRoleId && availableRoleId === roleId) {
      result.push({
        enterprise_crew_role_id: roleId,
        start_date: startDate,
        is_appointed: true,
      });
    } else {
      // role_idのもの（is_appointed: true）
      result.push({
        enterprise_crew_role_id: roleId,
        start_date: startDate,
        is_appointed: true,
      });

      // available_role_idがある場合（is_appointed: false）
      if (availableRoleId) {
        result.push({
          enterprise_crew_role_id: availableRoleId,
          start_date: startDate,
          is_appointed: false,
        });
      }
    }
  });

  return result;
};

/**
 * 船員マスタサービス
 */
const EnterpriseCrewsService = (() => {
  const fetch = async () => {
    const payload = {
      details: ["CREW_AVAILABLE_ROLE", "AFFILIATED_COMPANY"],
    };
    const res = await enterpriseCrewRepositoryV2.fetch(payload);
    if (!res || !res.status) {
      throw res;
    }
    let enterpriseCrews = res.data;

    console.log(enterpriseCrews);

    let crewList = enterpriseCrews.crews.map(crew => {
      return new CrewBasicInfo({
        id: crew.id,
        name: crew.name,
        name_kana: crew.name_kana,
        employment_status: crew.employment_status,
        affiliated_companies: crew.affiliated_companies,
        available_roles: transformAvailableRoles(crew.crew_available_roles),
        highest_rank_name: crew.highest_rank_name,
        joining_date: crew.joining_date,
        employee_number: crew.employee_number,
        union_member: crew.union_member,
        email_address: crew.email_address,
        phone_number: crew.phone_number,
        birth_date: crew.birth_date,
        postal_code: crew.postal_code,
        prefecture: crew.prefecture,
        address: crew.address,
        retirement_date: crew.retirement_date,
        memo: crew.memo,
      });
    });
    return crewList;
  };

  const fetchById = async id => {
    const payload = {
      details: ["CREW_AVAILABLE_ROLE", "AFFILIATED_COMPANY"],
    };
    const res = await enterpriseCrewRepositoryV2.fetchById(id, payload);
    if (!res || !res.status) {
      throw res;
    }
    let crew = res.data;
    return new CrewBasicInfo({
      id: crew.id,
      name: crew.name,
      name_kana: crew.name_kana,
      employment_status: crew.employment_status,
      affiliated_companies: crew.affiliated_companies,
      available_roles: transformAvailableRoles(crew.crew_available_roles),
      highest_rank_name: crew.highest_rank_name,
      joining_date: crew.joining_date,
      employee_number: crew.employee_number,
      union_member: crew.union_member,
      email_address: crew.email_address,
      phone_number: crew.phone_number,
      birth_date: crew.birth_date,
      postal_code: crew.postal_code,
      prefecture: crew.prefecture,
      address: crew.address,
      retirement_date: crew.retirement_date,
      memo: crew.memo,
    });
  };

  const updateById = async (id, payload) => {
    // available_rolesをcrew_available_roles形式に変換
    const transformedPayload = { ...payload };
    if (transformedPayload.available_roles) {
      transformedPayload.crew_available_roles = transformToCrewAvailableRoles(
        transformedPayload.available_roles
      );
      delete transformedPayload.available_roles;
    }

    // affiliated_companies を整形
    if (transformedPayload.affiliated_companies) {
      transformedPayload.affiliated_companies = normalizeAffiliatedCompanies(
        transformedPayload.affiliated_companies
      );
    }

    const res = await enterpriseCrewRepository.update(id, transformedPayload);
    if (!res || !res.status) {
      throw res;
    }
    let crew = res.data;
    return new CrewBasicInfo({
      id: crew.id,
      name: crew.name,
      name_kana: crew.name_kana,
      employment_status: crew.employment_status,
      affiliated_companies: crew.affiliated_companies,
      available_roles: transformAvailableRoles(crew.crew_available_roles),
      highest_rank_name: crew.highest_rank_name,
      joining_date: crew.joining_date,
      employee_number: crew.employee_number,
      union_member: crew.union_member,
      email_address: crew.email_address,
      phone_number: crew.phone_number,
      birth_date: crew.birth_date,
      postal_code: crew.postal_code,
      prefecture: crew.prefecture,
      address: crew.address,
      retirement_date: crew.retirement_date,
      memo: crew.memo,
    });
  };

  const add = async payload => {
    // available_rolesをcrew_available_roles形式に変換
    const transformedPayload = { ...payload };
    if (transformedPayload.available_roles) {
      transformedPayload.crew_available_roles = transformToCrewAvailableRoles(
        transformedPayload.available_roles
      );
      delete transformedPayload.available_roles;
    }

    // affiliated_companies を整形
    if (transformedPayload.affiliated_companies) {
      transformedPayload.affiliated_companies = normalizeAffiliatedCompanies(
        transformedPayload.affiliated_companies
      );
    }

    const res = await enterpriseCrewRepository.add(transformedPayload);
    if (!res || !res.status) {
      throw res;
    }
    return res.data;
  };

  const deleteById = async id => {
    const res = await enterpriseCrewRepository.delete(id);
    if (!res || !res.status) {
      throw res;
    }
    return res.data;
  };

  return {
    fetch,
    fetchById,
    updateById,
    add,
    deleteById,
  };
})();

/**
 * 船員役割マスターサービス
 */
const EnterpriseCrewRolesService = (() => {
  const fetch = async () => {
    const res = await enterpriseCrewRoleRepository.search();
    if (!res || !res.status) {
      throw res;
    }
    let crewRoles = res.data.roles.map(role => {
      return {
        id: role.id,
        sort_order: role.sort_order,
        label: role.name,
        department: role.department,
      };
    });
    return crewRoles;
  };
  return { fetch };
})();

export { EnterpriseCrewsService, EnterpriseCrewRolesService };
